import { FEATURE_FLAGS } from "@/data/globals";
import {
  ApplyRuleCost,
  CalcRule,
  GameSystems,
  IDataSpecialRule,
  ISpell,
  IUnit,
  RuleCostFlags,
  RuleCostFormula,
  RuleFormula,
  RuleFormulaType
} from "@/data/types";
import { isFeatureEnabled } from "@/env";
import { orderBy, uniq } from "lodash-es";
import { match } from "ts-pattern";
import { Result } from "typescript-result";
import ArmyBookRule from "../armyBook/ArmyBookRule";
import { getFlagsAsNames, isError, makeCopy, ruleFlagEnabled } from "../Helpers";
import CalcService, { getAverageModel, LOG_VERBOSE } from "./calcService";

const ENABLE_V360 = isFeatureEnabled(FEATURE_FLAGS.V3_6);

export type Unit = {
  name: string;
  quality: number;
  defense: number;
  tough?: number;
  models?: number;
  unitSize?: number;
  rules: string[];
  equipment: Weapon[];

  qualityCost?: number;
  defenseCost?: number;
  moraleCost?: number;
  speedAdvance?: number;
  speedRush?: number;
  normalised: boolean;
};

export type Weapon = {
  name?: string;
  range?: number | "melee";
  attacks: number;
  rules: string[];
  melee?: boolean;
  ranged?: boolean;
  assault?: boolean;
  rulesApplied?: string[];
  cost?: number;
};

export class CalcError extends Error {
  readonly type = "calc-error";
  constructor(
    public ruleName: string,
    public formulaType: string,
    public message: string
  ) {
    super(`Error in rule ${ruleName}, formula type ${formulaType}: ` + message);
  }
}

// Utility type to extract the correct `formula` function type based on the `type` parameter
type FormulaCallback<T extends RuleFormulaType> = Extract<RuleFormula, { type: T }>["fn"];

function clampStat(x: number) {
  return clamp(x, 1, 6);
}
function clamp(x: number, min: number, max: number) {
  return Math.min(max, Math.max(min, x));
}

// TODO: Should return undefined, but causes a lot of null refs...
function clone<T>(x: T): T {
  return x ? (JSON.parse(JSON.stringify(x)) as T) : ({} as any);
}

function sumBy<T>(arr: T[], selector: (x: T) => number | undefined) {
  return arr.reduce((total, next) => total + (selector(next) ?? 0), 0);
}

function toRulesObject(rules: { name: string; costFormula?: RuleCostFormula[] }[]): {
  [key: string]: CalcRule;
} {
  return rules.reduce((result, next) => {
    if (!next.costFormula) {
      return result;
    }

    return {
      ...result,
      [next.name]: {
        name: next.name,
        formula: next.costFormula
      } as CalcRule
    };
  }, {});
}

export const SPECIAL_SETTINGS = {
  GLOBAL_MULTIPLIER: "GLOBAL_MULTIPLIER",
  AuraSizes: "AuraSizes",
  _auraSizeBattle: "_auraSizeBattle",
  _auraSizeSkirmish: "_auraSizeSkirmish",
  AVG_SPELL_COST: "AVG_SPELL_COST",
  TOUGH_BASE_ADD: "TOUGH_BASE_ADD",
  TOUGH_BASE_FAC: "TOUGH_BASE_FAC",
  TOUGH_BASE_ADD_SKIRMISH: "TOUGH_BASE_ADD_SKIRMISH",
  TOUGH_BASE_FAC_SKIRMISH: "TOUGH_BASE_FAC_SKIRMISH",
  AutoWoundWeapon: "autoWoundWeapon",
  AutoWoundQuality: "AutoWoundQuality",
  AssaultMeleeMultiplier: "AssaultMeleeMultiplier",
  AssaultRangedMultiplier: "AssaultRangedMultiplier",
  AverageMeleeWeapon: "averageMeleeWeapon",
  AverageRangedWeapon: "averageRangedWeapon",
  CostPerRange: "costPerRange",
  QualityValues: "qualityValues",
  DefenseValues: "defenseValues",
  MoraleValues: "moraleValues",
  SpeedValues: "speedValues",
  Spells: "spells",
  SKIRMISH_SIZE_5_THRESHOLD: "SKIRMISH_SIZE_5_THRESHOLD",
  SKIRMISH_SIZE_10_THRESHOLD: "SKIRMISH_SIZE_10_THRESHOLD",
  SKIRMISH_OFFENSIVE_MULT: "SKIRMISH_OFFENSIVE_MULT",
  SKIRMISH_DEFENSIVE_MULT: "SKIRMISH_DEFENSIVE_MULT",
  WEAPON_RANGE_WEIGHTS: "weaponRangeWeights",
  QUEST_UPGRADE_COST_MULT: "QUEST_UPGRADE_COST_MULT",
  QUEST_VALUES: "QuestValues",
  SKIRMISH_VALUES: "SkirmishValues",
  SIZE_1_MINCOST: (gs: GameSystems) =>
    gs === GameSystems.aofs ? "SKIRMISH_SIZE_1_MINCOST" : "FIREFIGHT_SIZE_1_MINCOST",
  SIZE_3_MAXCOST: (gs: GameSystems) =>
    gs === GameSystems.aofs ? "SKIRMISH_SIZE_3_MAXCOST" : "FIREFIGHT_SIZE_3_MAXCOST"
};

export const CALC_SETTINGS_HELP_TEXT = {
  [SPECIAL_SETTINGS.SKIRMISH_SIZE_5_THRESHOLD]:
    "The number of pts at which units of 3 or 5 models are converted to units of [1] instead of [3]",
  [SPECIAL_SETTINGS.AuraSizes]: {
    battle: "How many models in the Battle Aura Unit",
    skirmish: "How many models in the Skirmish Aura Unit",
    skirmishAuraBuffUnitCount: "How many units affected by Auras/Buffs in Skirmish"
  }
};

export default class Calculator {
  public calcService: CalcService;

  // Recursion troubleshooting...
  private unitDepth: number = 0;
  private weaponDepth: number = 0;
  private avgUnitCost = 0;

  public settings: object;
  public averageUnit: Unit;
  public unitRules: { [key: string]: CalcRule };
  public weaponRules: { [key: string]: CalcRule };

  constructor(
    settings: object,
    unitRules: IDataSpecialRule[],
    weaponRules: IDataSpecialRule[],
    private unitLibrary: IUnit[],
    private _skirmish: boolean = false
  ) {
    this.settings = settings;
    this.unitRules = toRulesObject(unitRules);
    this.weaponRules = toRulesObject(weaponRules);
    this.averageUnit = getAverageModel(this.settings, _skirmish);
    this.avgUnitCost = this.unitCost(this.averageUnit).value; // TODO: Would this ever error...?
  }

  public setUnitRules(unitRules: (IDataSpecialRule | ISpell)[]) {
    this.unitRules = toRulesObject(unitRules);
  }

  public setUnits(units: IUnit[]) {
    this.unitLibrary = units;
  }

  private getUnitSpecialRule(ruleName: string): CalcRule {
    return this.unitRules[ruleName] || ({} as any);
  }

  private getWeaponSpecialRule(ruleName: string): CalcRule {
    return this.weaponRules[ruleName] || ({} as any);
  }

  // #region Helpers

  private currentRules: string[] = [];
  private currentRuleFlags: RuleCostFlags[] = [];
  private get currentRulesLog() {
    return this.currentRules.join(", ");
  }

  private applyFormula<T extends RuleFormulaType>(
    rule: CalcRule,
    type: T,
    callback: (formula: FormulaCallback<T>) => void
  ) {
    const isFlagActive = (flag: RuleCostFlags) => this.currentRuleFlags.some((x) => ruleFlagEnabled(x, flag));

    const ruleFormula = rule.formula?.filter((x) => x.type === type) || [];
    const doLog = LOG_VERBOSE && rule.name !== "Tough";

    if (ruleFormula.length && doLog) {
      this.currentRules.push(rule.name);
    }

    for (const formula of ruleFormula) {
      const isOnSixFormula = ruleFlagEnabled(formula.flags || 0, RuleCostFlags.OnSix);
      const isOnStrikeBackFormula = ruleFlagEnabled(formula.flags || 0, RuleCostFlags.OnStrikeBack);
      const isOnChargeFormula = ruleFlagEnabled(formula.flags || 0, RuleCostFlags.OnCharge);

      if (isOnSixFormula && isFlagActive(RuleCostFlags.SixMultiplier)) continue;
      if (isOnStrikeBackFormula && isFlagActive(RuleCostFlags.OnCharge)) {
        if (doLog) console.log(`Skipping ${rule.name}`);
        continue;
      }
      if (isOnChargeFormula && isFlagActive(RuleCostFlags.OnStrikeBack)) {
        if (doLog) console.log(`Skipping ${rule.name}`);
        continue;
      }

      if (formula.flags) this.currentRuleFlags.push(formula.flags);

      if (doLog) {
        console.group(
          `applyFormula ${rule.name}, ${formula.type}, flags:`,
          this.currentRuleFlags.map((x) => getFlagsAsNames(x).join(", "))
        );
      }

      const result = Result.try(() => {
        callback(formula.fn as FormulaCallback<T>);
      });
      if (doLog) {
        console.groupEnd();
      }

      if (formula.flags) this.currentRuleFlags.pop();

      if (isError(result)) {
        if (doLog) this.currentRules.pop();
        debugger;
        return Result.error(new CalcError(rule.name, formula.type, result.error.message));
      }
    }

    if (ruleFormula.length && doLog) {
      this.currentRules.pop();
    }
    return Result.ok();
  }

  private getUnitStatCost(unit: Unit, type: "quality" | "defense" | "morale") {
    if (
      !unit ||
      (type === "quality" && typeof unit.quality == "undefined") ||
      (type === "defense" && typeof unit.defense === "undefined") ||
      (type === "morale" && typeof unit.quality === "undefined") // Morale uses unit quality
    )
      return 0;

    const key = type === "defense" ? "defense" : "quality";
    const value = clampStat(unit[key] || 0);
    return getCostForStat(this.settings, value, type);
  }

  // This modifies the object so it should only be called with copies of units
  public addCalculatedPropertiesToUnit(unit: Unit) {
    unit.qualityCost = getCostForStat(this.settings, unit.quality || 0, "quality");
    unit.moraleCost = getCostForStat(this.settings, unit.quality || 0, "morale");
    unit.defenseCost = getCostForStat(this.settings, unit.defense || 0, "defense");

    return unit;
  }

  // #endregion

  // #region Base Cost

  private getToughMultiplier(T: number) {
    const useSkirmish = ENABLE_V360 && this._skirmish;
    const toughBaseAdd =
      this.settings[useSkirmish ? SPECIAL_SETTINGS.TOUGH_BASE_ADD_SKIRMISH : SPECIAL_SETTINGS.TOUGH_BASE_ADD];
    const toughBaseFac =
      T *
      this.settings[useSkirmish ? SPECIAL_SETTINGS.TOUGH_BASE_FAC_SKIRMISH : SPECIAL_SETTINGS.TOUGH_BASE_FAC];
    return T * (T === 1 ? 1 : toughBaseAdd + toughBaseFac);
  }

  public speedCost(unit: Unit, values: { advance?: number; rush?: number; single?: number }) {
    const qc = (unit.tough || 1) * unit.qualityCost;
    const speedValues = this.settings[SPECIAL_SETTINGS.SpeedValues] || {
      multiplier: 1
    };
    const advance = (values.advance ?? 0) * speedValues.factorAdvance;
    const rush = (values.rush ?? 0) * speedValues.factorRush;
    const single = (values.single ?? 0) * speedValues.factorSingle;
    const cost = qc * (advance + rush + single);

    const multiplier = cost < 0 ? speedValues.multiplierNegative : speedValues.multiplier;
    return cost * multiplier;
  }

  public unitBaseCost(originalUnit: Unit) {
    const unit = clone(originalUnit);
    const moraleCost = this.getUnitStatCost(unit, "morale");
    const qualityCost = this.getUnitStatCost(unit, "quality");
    const defenseCost = this.getUnitStatCost(unit, "defense");

    const toughness = unit.tough || 1;
    const rules = (unit.rules ? unit.rules.slice() : []).map((x) => this.getUnitSpecialRule(x));

    const qualityOrDefenseIsOutOfBounds = !moraleCost || !defenseCost;
    if (qualityOrDefenseIsOutOfBounds)
      return Result.error(new CalcError(null, "unitBaseCost", "Quality or defense is out of bounds"));

    const { Advance, Rush, Enabled } = this.settings[SPECIAL_SETTINGS.SpeedValues] || { Enabled: false };
    const speedCost = Enabled
      ? this.speedCost({ ...unit, qualityCost }, { advance: Advance, rush: Rush })
      : 0;

    const useNewMorale = this.settings[SPECIAL_SETTINGS.MoraleValues]?.Enabled === true;

    const baseCost = (useNewMorale ? moraleCost : qualityCost / 2) + defenseCost + speedCost;
    const toughMultiplier = this.getToughMultiplier(toughness);
    let total = baseCost * toughMultiplier;

    // Apply baseCostMultiplier
    for (let rule of rules) {
      const result = this.applyFormula(rule, "baseCostMultiplier", (fn) => {
        total *= (fn as (unit: Unit) => number)(unit);
      });

      if (isError(result)) return result;
    }

    return Result.ok(total);
  }

  public unitMoraleCost(unit: Unit) {
    const moraleCost = this.unitMoraleStatCost(unit);
    return moraleCost * this.getToughMultiplier(unit.tough || 1);
  }

  // Same as above but without T mult
  public unitMoraleStatCost(unit: Unit) {
    const useNewMorale = this.settings[SPECIAL_SETTINGS.MoraleValues]?.Enabled === true;
    const moraleCost = this.getUnitStatCost(unit, "morale");
    const qualityCost = this.getUnitStatCost(unit, "quality");
    if (!moraleCost) return 0;
    return useNewMorale ? moraleCost : qualityCost / 2;
  }

  public unitDefenseCost(unit: Unit) {
    const defenseCost = this.getUnitStatCost(unit, "defense");
    if (!defenseCost) return 0;
    return defenseCost * this.getToughMultiplier(unit.tough || 1);
  }

  public weaponBaseCost(weapon: Weapon) {
    if (LOG_VERBOSE) {
      console.group("weaponBaseCost " + weapon.name);
    }

    const range = weapon.range || "melee";
    const attacks = Math.max(weapon.attacks || 1, 1);
    const rules = (weapon.rules ? weapon.rules.slice() : []).map((x) => this.getWeaponSpecialRule(x));
    let total = getCostPerRange(this.settings, range + "") * attacks;

    // Apply baseCostMultiplier
    for (let rule of rules) {
      const result = this.applyFormula(rule, "baseCostMultiplier", (fn) => {
        total *= (fn as (weapon: Weapon) => number)(weapon);
      });

      if (isError(result)) {
        if (LOG_VERBOSE) console.groupEnd();
        return result;
      }
    }

    // Apply weaponBaseCost
    for (let rule of rules) {
      const result = this.applyFormula(rule, "weaponBaseCost", (fn) => {
        total += fn(weapon);
      });

      if (isError(result)) {
        if (LOG_VERBOSE) console.groupEnd();
        return result;
      }
    }

    if (LOG_VERBOSE) {
      console.log("weaponBaseCost", total.toFixed(2));
      console.groupEnd();
    }

    return Result.ok(total);
  }

  // #endregion

  // #region formula: cost

  private unitRulesCost(originalUnit: Unit) {
    const unit = clone(originalUnit);
    const unitRules = unit.rules || [];

    this.addCalculatedPropertiesToUnit(unit);

    const result = Result.all(...unitRules.map((x) => this.calculateUnitRuleCost(unit, x)));

    if (isError(result)) {
      return result;
    } else {
      return Result.ok(sumBy(result.value, (x) => x));
    }
  }

  private calculateUnitRuleCost(unit: Unit, ruleName: string) {
    const rule = this.getUnitSpecialRule(ruleName);

    let cost = 0;

    const result = this.applyFormula(rule, "cost", (fn) => {
      cost += (fn as (unit: Unit) => number)(unit) || 0;
    });

    if (isError(result)) return result;

    return Result.ok(cost);
  }

  // #endregion

  // #region formula: unitCost
  private wholeUnitSpecialRulesCost(originalUnit: Unit) {
    const result = Result.all(
      ...(originalUnit.rules || []).map((ruleName) => this.wholeUnitSpecialRuleCost(originalUnit, ruleName))
    );

    if (isError(result)) {
      return result;
    } else {
      return Result.ok(sumBy(result.value, (x) => x));
    }
  }

  private wholeUnitSpecialRuleCost(originalUnit: Unit, ruleName: string) {
    const unit = clone(originalUnit);

    this.addCalculatedPropertiesToUnit(unit);

    const rule = this.getUnitSpecialRule(ruleName);

    let cost = 0;
    const result = this.applyFormula(rule, "unitCost", (fn) => {
      cost += fn(unit);
    });
    if (isError(result)) return result;
    return Result.ok(cost);
  }
  // #endregion

  //#region applyRuleCosts

  private applyRuleCosts(baseUnit: Unit, _baseUnitCost: number) {
    const rules = makeCopy(baseUnit.rules)
      .map((x) => this.getUnitSpecialRule(x))
      .filter((x) => x.formula?.some((f) => f.type === "applyRuleCost"));
    if (rules.length === 0) return Result.ok(0);

    const cloneUnit = clone(baseUnit);

    let total = 0;

    //const coreRules = this.calcService.coreUnitRules;
    //cloneUnit.rules = baseUnit.rules.filter((r) => coreRules.includes(r));
    cloneUnit.rules = [];
    const costWithoutRuleResult = this.unitCost(cloneUnit);
    if (isError(costWithoutRuleResult)) return costWithoutRuleResult;

    // Now apply the formula to the "base unit"
    for (const rule of rules) {
      const result = this.applyFormula(rule, "applyRuleCost", (fn) => {
        const fnResult = (fn as (unit: Unit, baseUnit: Unit) => ApplyRuleCost)(cloneUnit, baseUnit);
        if (!fnResult) return;
        const modifiers = Array.isArray(fnResult) ? fnResult : [fnResult];
        for (const modifier of modifiers) {
          const unit = modifier.avgUnit ? clone(this.averageUnit) : clone(cloneUnit);
          const hasRule = unit.rules.includes(modifier.name);
          if (!hasRule) unit.rules.push(modifier.name);

          if (modifier.rating) {
            CalcService.applyRating(unit, { name: modifier.name, rating: modifier.rating });
          }

          if (LOG_VERBOSE) console.group(`applyRuleCost: Depth ${this.unitDepth}`, modifier.name);

          const costWithRuleResult = this.unitCost(unit);
          if (isError(costWithRuleResult)) throw costWithRuleResult.error;
          const compareToUnitCost = modifier.avgUnit ? this.avgUnitCost : costWithoutRuleResult.value;
          const diff = costWithRuleResult.value - compareToUnitCost;
          const result = diff * (modifier.multiplier ?? 1);
          total += result;

          if (LOG_VERBOSE) {
            console.log(modifier.name, { modifier, diff, result });
            console.groupEnd();
            console.log("");
          }
        }
      });

      if (isError(result)) return result;
    }

    return Result.ok(total);
  }

  //#endregion

  public unitRuleCost(originalUnit: Unit, ruleName: string) {
    const unit = clone(originalUnit);

    this.addCalculatedPropertiesToUnit(unit);

    const unitCostResult = this.calculateUnitRuleCost(unit, ruleName);
    const wholeUnitCostResult = this.wholeUnitSpecialRuleCost(unit, ruleName);

    if (isError(unitCostResult)) {
      return unitCostResult;
    } else if (isError(wholeUnitCostResult)) {
      return wholeUnitCostResult;
    }

    return Result.ok(unitCostResult.value + wholeUnitCostResult.value);
  }

  public unitCost(originalUnit: Unit, mutateUnit = false): Result.Error<CalcError> | Result.Ok<number> {
    if (LOG_VERBOSE) {
      console.group("unitCost " + originalUnit.name);
      console.log(originalUnit);
    }
    this.unitDepth++;

    const finish = <E>(result: Result.Error<E>) => {
      if (LOG_VERBOSE) {
        console.groupEnd();
      }
      this.unitDepth--;
      return result;
    };

    if (this.unitDepth > 10) {
      console.error("Unit recursion issue", originalUnit);
      debugger;
      this.unitDepth = 0;
      throw "Unit recursion limit exceeded";
    }

    const unit = mutateUnit ? originalUnit : clone(originalUnit);
    unit.rules = unit.rules || [];

    const equipment = unit.equipment || [];

    // Order this so that "Armor" rule comes before anything else
    const rulesForModifyUnit = unit.rules.map((x) => this.getUnitSpecialRule(x));
    const sortedRulesForModifyUnit = orderBy(rulesForModifyUnit, (x) => (x.name === "Armor" ? 0 : 1));
    for (const rule of sortedRulesForModifyUnit) {
      const result = this.applyFormula(rule, "properties", (fn) => fn(unit));
      if (isError(result)) return finish(result);
    }
    for (const rule of unit.rules.map((x) => this.getUnitSpecialRule(x))) {
      const result = this.applyFormula(rule, "addRules", (fn) => {
        unit.rules.push(...fn());
      });
      if (isError(result)) return finish(result);
    }

    unit.rules = uniq(unit.rules);

    // Weapons "modifyUnit"
    for (let weapon of equipment) {
      for (const rule of weapon.rules.map((x) => this.getWeaponSpecialRule(x))) {
        const result = this.applyFormula(rule, "modifyUnit", (fn) => {
          fn(unit, weapon);
        });
        if (isError(result)) return finish(result);
      }
    }

    const unitBaseCostResult = this.unitBaseCost(unit);
    if (isError(unitBaseCostResult)) return finish(unitBaseCostResult);
    const rulesCostResult = this.unitRulesCost(unit);
    if (isError(rulesCostResult)) return finish(rulesCostResult);
    const rulesUnitCostResult = this.wholeUnitSpecialRulesCost(unit);
    if (isError(rulesUnitCostResult)) return finish(rulesUnitCostResult);

    for (const e of equipment) {
      const result = this.weaponCost(e, unit);
      if (isError(result)) return finish(result);

      e.cost = result.value;
    }

    // const weaponsTotalResult = Result.all(...equipment.map((w) => this.weaponCost(w, unit)));
    // if (isError(weaponsTotalResult)) return finish(weaponsTotalResult);

    const weaponsTotal = sumBy(equipment, (x) => x.cost);

    const models = unit.models || 1;
    const costOfUnit = (unitBaseCostResult.value + rulesCostResult.value) * models;
    const fullCost = costOfUnit + weaponsTotal + rulesUnitCostResult.value;

    const extraRuleCostsResult = this.applyRuleCosts(unit, fullCost);
    if (isError(extraRuleCostsResult)) return finish(extraRuleCostsResult);

    const totalCost = fullCost + extraRuleCostsResult.value;

    if (LOG_VERBOSE) {
      console.log("unitCost", { totalCost, unit, extraRuleCostsResult });

      console.groupEnd();
      console.log(" ");
    }
    this.unitDepth--;

    return Result.ok(totalCost);
  }

  public weaponCost(
    originalWeapon: Weapon,
    originalUnit: Unit,
    beforeFinalMultiplier = false
  ): Result.Error<CalcError> | Result.Ok<number> {
    this.weaponDepth++;

    if (this.weaponDepth > 5) {
      this.weaponDepth = 0;
      console.error("Weapon recursion issue", originalUnit);
      return Result.error(new CalcError(null, "weaponCost", "Weapon recursion limit exceeded"));
    }

    if (ENABLE_V360 && originalWeapon.rules?.includes("Assault")) {
      // Reset the depth
      this.weaponDepth--;

      const assaultWeapon = { ...clone(originalWeapon), assault: true };
      // Remove assault rule from weapon so we don't recurse
      assaultWeapon.rules = assaultWeapon.rules.filter((x) => x !== "Assault");

      // Original weapon will be ranged, so we set the melee properties here to convert it
      const meleeWeapon = { ...clone(assaultWeapon), range: "melee", melee: true, ranged: false } as Weapon;
      const rangedWeapon = clone(assaultWeapon);

      const meleeMult = this.settings[SPECIAL_SETTINGS.AssaultMeleeMultiplier];
      const rangedMult = this.settings[SPECIAL_SETTINGS.AssaultRangedMultiplier];

      // Half the cost of the melee weapon
      const meleeCostResult = this.weaponCost(meleeWeapon, originalUnit, beforeFinalMultiplier);
      if (isError(meleeCostResult)) return meleeCostResult;

      // Full ranged cost of the weapon
      const rangedCostResult = this.weaponCost(rangedWeapon, originalUnit, beforeFinalMultiplier);
      if (isError(rangedCostResult)) return rangedCostResult;

      // Sum the melee and ranged components
      return Result.ok(meleeCostResult.value * meleeMult + rangedCostResult.value * rangedMult);
    }

    const weapon: Weapon = clone({
      range: "melee",
      ...originalWeapon,
      rules: originalWeapon.rules || [],
      rulesApplied: originalWeapon.rulesApplied || []
    });

    if (LOG_VERBOSE) {
      console.group(`weaponCost ${originalWeapon.name} (Depth ${this.weaponDepth})`, weapon);
      console.log(this.currentRulesLog);
    }

    const finish = <E>(result: Result.Error<E>) => {
      if (LOG_VERBOSE) {
        console.groupEnd();
      }
      this.weaponDepth--;
      return result;
    };

    const unit = clone(originalUnit);

    for (const rule of (weapon.rules || []).map((x) => this.getWeaponSpecialRule(x))) {
      const result = this.applyFormula(rule, "initialise", (fn) => {
        if (!weapon.rulesApplied.includes(rule.name)) {
          (fn as (weapon: Weapon, unit: Unit) => void)(weapon, unit);
          weapon.rulesApplied.push(rule.name);
        }
      });
      if (isError(result)) return finish(result);
    }

    for (const rule of (weapon.rules || []).map((x) => this.getWeaponSpecialRule(x))) {
      const result = this.applyFormula(rule, "addRules", (fn) => {
        const addRules = fn().filter((x) => !weapon.rules.includes(x));
        weapon.rules = uniq(weapon.rules.concat(...addRules));
      });
      if (isError(result)) return finish(result);
    }

    for (const rule of (weapon.rules || []).map((x) => this.getWeaponSpecialRule(x))) {
      const result = this.applyFormula(rule, "weaponProperties", (fn) => {
        if (!weapon.rulesApplied.includes(rule.name)) {
          fn(weapon);
          weapon.rulesApplied.push(rule.name);
        }
      });
      if (isError(result)) return finish(result);
    }

    const unitRules = (unit?.rules || []).map((x) => this.getUnitSpecialRule(x));

    // see if any of the weapon's rules modify the quality of the unit
    const rulesForQuality = weapon.rules.map((wr) => this.getWeaponSpecialRule(wr));
    for (const rule of rulesForQuality) {
      // Doesn't actually make sense for this one to be in a loop...
      const result = this.applyFormula(rule, "newQuality", (fn) => {
        // Don't re-apply this if it's already applied...
        if (!weapon.rulesApplied.includes(rule.name)) {
          unit.quality = fn(unit.quality, weapon);
          weapon.rulesApplied.push(rule.name);
        }
      });
      if (isError(result)) return finish(result);
    }

    // Only apply these if we're not already in a applyRuleCost calculation
    if (!this.applyRuleCostActive)
      for (const rule of unitRules) {
        const result = this.applyFormula(rule, "unitPropertiesForWeapons", (fn) => {
          fn(unit, weapon);
        });
        if (isError(result)) return finish(result);

        const modifyWeaponsResult = this.applyFormula(rule, "modifyWeapons", (fn) => {
          fn(weapon);
          // Remove the rule so it doesn't apply recursively
          unit.rules.splice(unit.rules.indexOf(rule.name), 1);
        });
        if (isError(modifyWeaponsResult)) return finish(modifyWeaponsResult);
      }

    const weaponBaseCostResult = this.weaponBaseCost(weapon);
    if (isError(weaponBaseCostResult)) return finish(weaponBaseCostResult);

    let baseCost = weaponBaseCostResult.value;

    try {
      for (const rule of unitRules) {
        const result = this.applyFormula(rule, "weaponBaseCostMultiplier", (fn) => {
          baseCost *= fn(weapon);
        });
        if (isError(result)) return finish(result);
      }
    } catch {
      console.log(JSON.stringify({ unit, unitRules }, null, 2));
    }

    for (const rule of unitRules) {
      const result = this.applyFormula(rule, "weaponBaseCost", (fn) => {
        baseCost += fn(weapon);
      });
      if (isError(result)) return finish(result);
    }

    // Used to override rules like Reliable/Sniper when calculating for a specific quality
    if ((unit as any).finalQuality) {
      unit.quality = (unit as any).finalQuality;
    }

    //TODO: need a test that necessitates this. And it should be in a function.
    unit.quality = clampStat(unit.quality);

    const qualityCost = getCostForStat(this.settings, unit.quality, "quality");

    let total = baseCost * qualityCost;

    const costResult = Result.all(
      ...weapon.rules
        .map((wr) => this.getWeaponSpecialRule(wr))
        .map((rule) => {
          let cost = 0;

          const result = this.applyFormula(rule, "cost", (fn) => {
            cost += (fn as (weapon: Weapon, unit: Unit) => number)(weapon, unit) || 0;
          });
          if (isError(result)) return result;

          return Result.ok(cost);
        })
    );
    if (isError(costResult)) return finish(costResult);

    total += sumBy(costResult.value, (x) => x);

    const unitRulesCostResult = Result.all(
      ...unitRules.map((rule) => {
        let cost = 0;
        const result = this.applyFormula(rule, "weaponCost", (fn) => {
          cost += fn(weapon, unit) || 0;
        });
        if (isError(result)) return result;

        return Result.ok(cost);
      })
    );
    if (isError(unitRulesCostResult)) return finish(unitRulesCostResult);

    total += sumBy(unitRulesCostResult.value, (x) => x);

    const weaponRuleCostMultipliersResult = this.applyRuleCostsWeapon(weapon, unit, total);

    if (isError(weaponRuleCostMultipliersResult)) return finish(weaponRuleCostMultipliersResult);

    total += weaponRuleCostMultipliersResult.value;

    if (beforeFinalMultiplier) {
      if (LOG_VERBOSE) console.groupEnd();
      this.weaponDepth--;
      return Result.ok(total);
    }

    for (const rule of (weapon.rules || []).map((x) => this.getWeaponSpecialRule(x))) {
      const result = this.applyFormula(rule, "finalCostMultiplier", (fn) => {
        const multiplier = fn(weapon, unit);
        total *= multiplier;
      });
      if (isError(result)) return finish(result);
    }

    if (LOG_VERBOSE) {
      console.log("weaponCost", total.toFixed(2));
      console.groupEnd();
    }

    this.weaponDepth--;

    return Result.ok(total);
  }

  //#region applyRuleCosts

  private applyRuleCostActive = false;

  private applyRuleCostsWeapon(originalWeapon: Weapon, originalUnit: Unit, baseWeaponCost: number) {
    const weaponRules = makeCopy(originalWeapon.rules).map((x) => this.getWeaponSpecialRule(x));
    //if (weaponRules.length === 0) return Result.ok(0);

    const baseWeapon = clone(originalWeapon);
    const baseUnit = clone(originalUnit);

    let total = 0;
    // Remove any rule that has a "applyRuleCost" from the cloned unit
    for (const rule of weaponRules) {
      if (!Array.isArray(rule.formula)) {
        continue;
      }
      if (rule.formula.some((x) => x.type === "applyRuleCost")) {
        baseWeapon.rules.splice(baseWeapon.rules.indexOf(rule.name), 1);
      }
    }

    const process = (unit, fn) => {
      const modifier = (fn as (weapon: Weapon, unit: Unit) => ApplyRuleCost)(baseWeapon, unit);
      if (!modifier) return;
      this.applyRuleCostActive = true;
      const weapon = clone(baseWeapon);
      if (modifier.meleeOnly && weapon.ranged) return;
      if (modifier.rangedOnly && weapon.melee) return;
      const hasRule = weapon.rules.includes(modifier.name);
      if (modifier.unique && hasRule) return;

      if (modifier.rating) {
        if (!hasRule) weapon.rules.push(modifier.name);
        CalcService.applyRating(weapon, { name: modifier.name, rating: modifier.rating });
      } else {
        weapon.rules.push(modifier.name);
      }

      this.applyRuleCostActive = true;
      try {
        const weaponCostResult = this.weaponCost(weapon, unit, true);
        if (isError(weaponCostResult)) throw weaponCostResult.error;
        total += (weaponCostResult.value - baseWeaponCost) * (modifier.multiplier ?? 1);
      } finally {
        this.applyRuleCostActive = false;
      }
    };

    // Now apply the formula to the "base unit"
    for (const rule of weaponRules) {
      const result = this.applyFormula(rule, "applyRuleCost", (fn) => process(baseUnit, fn));
      if (isError(result)) return result;
    }

    const unitRules = originalUnit.rules?.map((x) => this.getUnitSpecialRule(x)) ?? [];
    for (const rule of unitRules) {
      const unit = makeCopy(baseUnit);
      unit.rules = unit.rules.filter((r) => r !== rule.name); // Remove the rule so it doesn't apply recursively
      const result = this.applyFormula(rule, "applyRuleCostWeapon", (fn) => process(unit, fn));
      if (isError(result)) return result;
    }

    return Result.ok(total);
  }

  //#endregion
}

export const getCostForStat = (
  settings: object,
  key: number,
  type: "morale" | "quality" | "defense"
): number => {
  if (key < 1) {
    return 99999; // Catch errors?
  }

  const values = match(type)
    .with("quality", () => settings[SPECIAL_SETTINGS.QualityValues])
    .with("defense", () => settings[SPECIAL_SETTINGS.DefenseValues])
    .with("morale", () => settings[SPECIAL_SETTINGS.MoraleValues] || settings[SPECIAL_SETTINGS.QualityValues]) // Fallback
    .exhaustive();

  if (key > 6) key = 6;
  if ((key * 100) % 100 !== 0) {
    return interpolate(values, key) * values.multiplier;
  }

  const value = values[key];
  if (!value) {
    console.log("No quality found for key", key);
  }
  return value * values.multiplier;
};

const getCostPerRange = (settings: object, key: string) => {
  const value = settings["costPerRange"][key];
  const multiplier = settings["costPerRange"].multiplier || 1;
  return (value || 3.5) * multiplier;
};

function interpolate(array: number[], index: number): number {
  const minStat = Math.floor(index);
  const maxStat = Math.ceil(index);
  const min = array[minStat];
  const max = array[maxStat];
  const prc = index - minStat;
  return min + (max - min) * prc;
}

//#endregion

export function addRuleTo(obj, ruleName: string, value?) {
  if (!obj.rules) obj.rules = [];

  if (!obj.rules.includes(ruleName)) obj.rules.push(ruleName);

  const ruleAddsProperty = typeof value === "number";
  const propName = ruleName.toLowerCase();
  const propertyAlreadyHadValue = typeof obj[propName] === "number";

  if (ruleAddsProperty) {
    if (propertyAlreadyHadValue) obj[propName] += value;
    else obj[propName] = value;
  }
}

function weap(range: number, attacks: number, rules: string[]) {
  return CalcService.normalizeWeapon({
    range,
    attacks,
    specialRules: rules.map((x) => ArmyBookRule.Parse(x))
  });
}

const registerHelpers = (x) => {
  try {
    (x as any).addRuleTo = addRuleTo;
    (x as any).noop = function () {};
    (x as any).weap = weap;
    (x as any).interpolate = interpolate;
    (x as any).clamp = clamp;
  } catch {} // suppress errors
};
try {
  registerHelpers(window);
} catch {}
try {
  registerHelpers(global);
} catch {}
